import * as XLSX from "xlsx-js-style";
import { httpPost } from "../../../../../../Utils/apis/apis";
import { getHttpConfig } from "../../../../../../Utils/Helpers/common";
import { excelFileName } from "../../../../../../Utils/config/config";
import { replaceSpecialCharacters } from "../../../../Atoms/Surfaces/Cards/A_Card.helper";

export const DOWNLOAD_ALL_LABELS = {
  BUTTON: "Download All",
  DOWNLOADING: "Downloading...",
  ERROR: "Something went wrong while downloading the file",
  NO_DATA: "No downloadable cards found on this page",
  DEFAULT_SHEET_NAME: "Sheet",
};

export const DOWNLOAD_EXCEL_API = "/ChartData/download-excel";

export const CHART_DATA_API = "/ChartData/getchartdata";

const SECTION_GAP_ROWS = 2;

export const EXPORT_ROW_TYPES = {
  TITLE: "title",
  HEADER: "header",
  TOTAL: "total",
  DATA: "data",
};

const EXPORT_COLORS = {
  DARK_FILL: "1E293B",
  LIGHT_TEXT: "FFFFFF",
  TOTAL_FILL: "E2E8F0",
  STRIPE_FILL: "F1F5F9",
  GRID_LINE: "CBD5E1",
  TOTAL_LINE: "64748B",
};

const GRID_BORDER = { style: "thin", color: { rgb: EXPORT_COLORS.GRID_LINE } };

const TOTAL_BORDER = { style: "thin", color: { rgb: EXPORT_COLORS.TOTAL_LINE } };

const EXPORT_GRID_BORDERS = {
  top: GRID_BORDER,
  bottom: GRID_BORDER,
  left: GRID_BORDER,
  right: GRID_BORDER,
};

const EXPORT_CELL_STYLES = {
  [EXPORT_ROW_TYPES.TITLE]: {
    font: { bold: true, sz: 12, color: { rgb: EXPORT_COLORS.LIGHT_TEXT } },
    fill: { patternType: "solid", fgColor: { rgb: EXPORT_COLORS.DARK_FILL } },
    alignment: { vertical: "center", indent: 1 },
  },
  [EXPORT_ROW_TYPES.HEADER]: {
    font: { bold: true, color: { rgb: EXPORT_COLORS.LIGHT_TEXT } },
    fill: { patternType: "solid", fgColor: { rgb: EXPORT_COLORS.DARK_FILL } },
    alignment: { horizontal: "center", vertical: "center" },
  },
  [EXPORT_ROW_TYPES.TOTAL]: {
    font: { bold: true },
    fill: { patternType: "solid", fgColor: { rgb: EXPORT_COLORS.TOTAL_FILL } },
    border: {
      ...EXPORT_GRID_BORDERS,
      top: TOTAL_BORDER,
      bottom: TOTAL_BORDER,
    },
  },
};

const EXPORT_ROW_HEIGHTS = {
  [EXPORT_ROW_TYPES.TITLE]: 26,
  [EXPORT_ROW_TYPES.HEADER]: 20,
};

const EXPORT_COLUMN_WIDTH = { MIN: 10, MAX: 50, PADDING: 2 };

const EXPORT_NUMBER_FORMAT = "#,##0.00_);(#,##0.00)";

const TOTAL_ROW_CLASS_HINTS = ["total_row", "bold", "TopBottomBorder"];

const CARD_CONTENT_TYPES = {
  CHART: "chart",
  CUSTOM_CARD: "customcard",
};

const PARAMETER_TOKENS = {
  "=crmid": "crmId",
  "=pageurl": "pageUrl",
};

const EXCEL_SHEET_NAME_LIMIT = 31;

const TEMPLATE_TOKEN_PATTERN = /{{\s*([^{}]+?)\s*}}/g;

export const isFlagEnabled = (value) =>
  value === true || String(value).toLowerCase() === "true";

const getPageData = (currentPage) => {
  const pageData =
    currentPage && currentPage.data !== undefined
      ? currentPage.data
      : currentPage;
  if (typeof pageData === "string") {
    try {
      return JSON.parse(pageData) || {};
    } catch (error) {
      return {};
    }
  }
  return pageData && typeof pageData === "object" ? pageData : {};
};

export const resolveTemplateTokens = (text, currentPage) => {
  if (typeof text !== "string" || !text.includes("{{")) {
    return text;
  }
  const pageData = getPageData(currentPage);
  return text
    .replace(TEMPLATE_TOKEN_PATTERN, (token, key) => {
      const value = pageData[key];
      return value === null || value === undefined ? "" : String(value);
    })
    .replace(/\s{2,}/g, " ")
    .replace(/[\s-]+$/, "")
    .trim();
};

export const buildDownloadFileName = (fileName, currentPage) => {
  const resolvedName = resolveTemplateTokens(
    String(fileName || excelFileName.EXCELFILE),
    currentPage
  );
  return replaceSpecialCharacters(resolvedName) || excelFileName.EXCELFILE;
};

const resolveParameterTokens = (parameters, params) => {
  const resolved = {};
  Object.entries(parameters || {}).forEach(([key, value]) => {
    const token =
      typeof value === "string"
        ? PARAMETER_TOKENS[value.toLowerCase()]
        : undefined;
    resolved[key] = token ? params?.[token] : value;
  });
  return resolved;
};

const getCardChartConfig = (card) => {
  const type = card?.content?.type?.toLowerCase();
  const cardContent = card?.content?.content;
  if (type === CARD_CONTENT_TYPES.CHART && cardContent?.chart?.id) {
    return {
      chartId: cardContent.chart.id,
      parameters: cardContent.chart.parameters,
    };
  }
  if (type === CARD_CONTENT_TYPES.CUSTOM_CARD && cardContent?.chartId) {
    return {
      chartId: cardContent.chartId,
      parameters: cardContent.parameters,
    };
  }
  return null;
};

const getWorksheetName = (card, index, usedNames) => {
  const configuredName =
    card?.header?.toolbar?.export?.worksheetName || card?.header?.title;
  const baseName = replaceSpecialCharacters(
    configuredName || `${DOWNLOAD_ALL_LABELS.DEFAULT_SHEET_NAME} ${index + 1}`
  ).slice(0, EXCEL_SHEET_NAME_LIMIT);
  let name = baseName;
  let suffix = 2;
  while (usedNames.has(name.toLowerCase())) {
    const suffixText = ` (${suffix})`;
    name =
      baseName.slice(0, EXCEL_SHEET_NAME_LIMIT - suffixText.length) +
      suffixText;
    suffix += 1;
  }
  usedNames.add(name.toLowerCase());
  return name;
};

export const buildDownloadAllPayload = (cards, params) => {
  const usedNames = new Set();
  return (cards || [])
    .filter(
      (card) =>
        !isFlagEnabled(card?.header?.toolbar?.export?.excludeFromDownloadAll)
    )
    .map((card, index) => {
      const chartConfig = getCardChartConfig(card);
      if (!chartConfig) {
        return null;
      }
      return {
        chartId: chartConfig.chartId,
        parameters: resolveParameterTokens(chartConfig.parameters, params),
        fileName: excelFileName.EXCELFILE,
        worksheetName: getWorksheetName(card, index, usedNames),
      };
    })
    .filter(Boolean);
};

const getChartConfigurations = (chartData) => {
  const configuration =
    chartData && chartData.chartConfiguration
      ? chartData.chartConfiguration.configuration
      : null;
  if (!configuration) {
    return {};
  }
  if (typeof configuration !== "string") {
    return configuration;
  }
  try {
    return JSON.parse(configuration) || {};
  } catch (error) {
    return {};
  }
};

const isTotalRow = (configurations, rowName) => {
  const name = String(rowName || "");
  const rowFormat =
    configurations && configurations.rowFormats
      ? configurations.rowFormats[name]
      : null;
  const rowClasses =
    rowFormat && rowFormat.classes ? String(rowFormat.classes) : "";
  if (TOTAL_ROW_CLASS_HINTS.some((hint) => rowClasses.includes(hint))) {
    return true;
  }
  const rowStyle =
    configurations && configurations.rowStyle
      ? configurations.rowStyle[name]
      : null;
  if (rowStyle && String(rowStyle.fontWeight || "").toLowerCase() === "bold") {
    return true;
  }
  return name.toLowerCase().includes("total");
};

const buildSectionRows = (sectionTitle, header, dataRows) => {
  const titleCells = [
    sectionTitle,
    ...Array(Math.max(header.length - 1, 0)).fill(""),
  ];
  return [
    { cells: titleCells, type: EXPORT_ROW_TYPES.TITLE },
    { cells: header, type: EXPORT_ROW_TYPES.HEADER },
    ...dataRows,
  ];
};

export const getChartSection = (chartData, sectionTitle) => {
  const configurations = getChartConfigurations(chartData);
  const data = (chartData && chartData.data) || chartData || {};
  const categories = data.categories || [];
  const series = data.series || [];
  if (series.length > 0) {
    const header = ["", ...categories];
    const rows = series.map((row) => {
      const rowName = Array.isArray(row.name) ? row.name[0] : row.name;
      const values = (row.data || []).map((value) =>
        value === null || value === undefined ? "" : value
      );
      return {
        cells: [rowName, ...values],
        type: isTotalRow(configurations, rowName)
          ? EXPORT_ROW_TYPES.TOTAL
          : EXPORT_ROW_TYPES.DATA,
      };
    });
    return buildSectionRows(sectionTitle, header, rows);
  }
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === "object") {
    const header = Object.keys(data[0]);
    const rows = data.map((row) => ({
      cells: header.map((key) =>
        row[key] === null || row[key] === undefined ? "" : row[key]
      ),
      type: isTotalRow(configurations, row[header[0]])
        ? EXPORT_ROW_TYPES.TOTAL
        : EXPORT_ROW_TYPES.DATA,
    }));
    return buildSectionRows(sectionTitle, header, rows);
  }
  return [];
};

export const getChartSectionRows = (chartData, sectionTitle) =>
  getChartSection(chartData, sectionTitle).map((row) => row.cells);

const getCellDisplayWidth = (value) => {
  if (typeof value !== "number") {
    return String(value ?? "").length;
  }
  const formatted = Math.abs(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatted.length + (value < 0 ? 2 : 0);
};

const getCellStyle = (row, columnIndex, isStripedRow) => {
  if (row.type === EXPORT_ROW_TYPES.TITLE) {
    return columnIndex === 0 ? { ...EXPORT_CELL_STYLES[row.type] } : {};
  }
  if (row.type === EXPORT_ROW_TYPES.HEADER) {
    const style = { ...EXPORT_CELL_STYLES[row.type] };
    if (columnIndex === 0) {
      style.alignment = { ...style.alignment, horizontal: "left", indent: 1 };
    }
    return style;
  }
  if (row.type === EXPORT_ROW_TYPES.TOTAL) {
    return { ...EXPORT_CELL_STYLES[row.type] };
  }
  const style = { border: EXPORT_GRID_BORDERS };
  if (isStripedRow) {
    style.fill = {
      patternType: "solid",
      fgColor: { rgb: EXPORT_COLORS.STRIPE_FILL },
    };
  }
  return style;
};

const applyWorksheetStyles = (worksheet, sectionRows) => {
  let dataRowIndex = 0;
  sectionRows.forEach((row, rowIndex) => {
    if (row.type !== EXPORT_ROW_TYPES.DATA) {
      dataRowIndex = 0;
    }
    const hasCells = row.cells.length > 0;
    const isStripedRow =
      row.type === EXPORT_ROW_TYPES.DATA && hasCells && dataRowIndex % 2 === 1;
    if (row.type === EXPORT_ROW_TYPES.DATA && hasCells) {
      dataRowIndex += 1;
    }
    row.cells.forEach((cellValue, columnIndex) => {
      const cellRef = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = worksheet[cellRef];
      if (!cell) {
        return;
      }
      const cellStyle = getCellStyle(row, columnIndex, isStripedRow);
      if (typeof cellValue === "number") {
        cell.z = EXPORT_NUMBER_FORMAT;
        cellStyle.numFmt = EXPORT_NUMBER_FORMAT;
      }
      if (Object.keys(cellStyle).length > 0) {
        cell.s = cellStyle;
      }
    });
  });
  worksheet["!rows"] = sectionRows.map((row) => {
    const heightPoints = EXPORT_ROW_HEIGHTS[row.type];
    return heightPoints ? { hpt: heightPoints } : {};
  });
};

export const downloadAllAsSingleSheet = async (
  cards,
  params,
  fileName,
  sheetName
) => {
  const payload = buildDownloadAllPayload(cards, params);
  if (payload.length === 0) {
    throw new Error(DOWNLOAD_ALL_LABELS.NO_DATA);
  }
  const responses = await Promise.all(
    payload.map((entry) =>
      httpPost(CHART_DATA_API, {
        ChartId: entry.chartId,
        parameters: entry.parameters,
      }).catch(() => null)
    )
  );
  const sectionRows = [];
  responses.forEach((response, index) => {
    const section = getChartSection(response, payload[index].worksheetName);
    if (section.length === 0) {
      return;
    }
    if (sectionRows.length > 0) {
      for (let gap = 0; gap < SECTION_GAP_ROWS; gap += 1) {
        sectionRows.push({ cells: [], type: EXPORT_ROW_TYPES.DATA });
      }
    }
    sectionRows.push(...section);
  });
  if (sectionRows.length === 0) {
    throw new Error(DOWNLOAD_ALL_LABELS.ERROR);
  }
  const aoa = sectionRows.map((row) => row.cells);
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  applyWorksheetStyles(worksheet, sectionRows);
  const columnCount = Math.max(...aoa.map((row) => row.length));
  worksheet["!cols"] = Array.from({ length: columnCount }, (_, columnIndex) => {
    const contentWidth = Math.max(
      ...aoa.map((row) => getCellDisplayWidth(row[columnIndex]))
    );
    return {
      wch: Math.min(
        Math.max(
          contentWidth + EXPORT_COLUMN_WIDTH.PADDING,
          EXPORT_COLUMN_WIDTH.MIN
        ),
        EXPORT_COLUMN_WIDTH.MAX
      ),
    };
  });
  const workbook = XLSX.utils.book_new();
  const worksheetTabName = replaceSpecialCharacters(
    sheetName || fileName || excelFileName.EXCELFILE
  ).slice(0, EXCEL_SHEET_NAME_LIMIT);
  XLSX.utils.book_append_sheet(workbook, worksheet, worksheetTabName);
  XLSX.writeFile(workbook, `${fileName || excelFileName.EXCELFILE}.xlsx`);
};

export const downloadExcelFromApi = async (payload, fileName) => {
  const config = getHttpConfig();
  const response = await httpPost(DOWNLOAD_EXCEL_API, payload, {
    ...config,
    responseType: "blob",
  });
  if (!response) {
    throw new Error(DOWNLOAD_ALL_LABELS.ERROR);
  }
  const url = window.URL.createObjectURL(new Blob([response]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `${fileName || excelFileName.EXCELFILE}.xlsx`
  );
  link.click();
  window.URL.revokeObjectURL(url);
};

export const downloadAllAsExcel = async (cards, params, fileName) => {
  const payload = buildDownloadAllPayload(cards, params);
  if (payload.length === 0) {
    throw new Error(DOWNLOAD_ALL_LABELS.NO_DATA);
  }
  await downloadExcelFromApi(payload, fileName);
};
